import { PARTY_CENTRAL_LIMITS } from '@airhead-games/shared/types';
import type {
  PartyCentralAxis,
  PartyCentralDrilldownResponse,
  PartyCentralEntitiesRequest,
  PartyCentralEntitiesResponse,
  PartyCentralGrain,
  PartyCentralMemberMode,
  PartyCentralMetaResponse,
  PartyCentralMetric,
  PartyCentralPageAxis,
  PartyCentralProbeResponse,
  PartyCentralQueryResponse,
} from '@airhead-games/shared/types';

import { baseFetch, type BaseFetchOptions } from './baseFetch';

export class PartyCentralCaptureMismatchError extends Error {
  readonly expectedCaptureId: string;
  readonly actualCaptureId: string;

  constructor(expectedCaptureId: string, actualCaptureId: string) {
    super('Party Central data advanced to a newer capture.');
    this.name = 'PartyCentralCaptureMismatchError';
    this.expectedCaptureId = expectedCaptureId;
    this.actualCaptureId = actualCaptureId;
  }
}

export interface PartyCentralQueryInput {
  measure: PartyCentralMetric;
  x?: PartyCentralAxis;
  y?: PartyCentralAxis;
  grain?: PartyCentralGrain;
  from: string;
  to: string;
  game?: readonly string[];
  streamer?: readonly string[];
  language?: readonly string[];
  xMode?: PartyCentralMemberMode;
  yMode?: PartyCentralMemberMode;
  xIds?: readonly string[];
  yIds?: readonly string[];
  pageAxis?: PartyCentralPageAxis;
  page?: number;
}

export type CanonicalPartyCentralQueryInput = Omit<
  PartyCentralQueryInput,
  'game' | 'streamer' | 'language' | 'xIds' | 'yIds' | 'page'
> & {
  game: string[];
  streamer: string[];
  language: string[];
  hourUtc: number[];
  dayOfWeekUtc: number[];
  xIds: string[];
  yIds: string[];
  page: number;
};

export type PartyCentralEntitiesInput = Omit<
  PartyCentralEntitiesRequest,
  'ids' | 'limit'
> & {
  ids?: readonly string[];
  limit?: number;
};

export interface CanonicalPartyCentralEntitiesInput extends Omit<
  PartyCentralEntitiesInput,
  'ids' | 'limit'
> {
  ids: string[];
  limit: number;
}

export interface PartyCentralDrilldownInput {
  from: string;
  to: string;
  captureId?: string;
  game?: readonly string[];
  streamer?: readonly string[];
  language?: readonly string[];
  hourUtc?: readonly number[];
  dayOfWeekUtc?: readonly number[];
  limit?: number;
}

export type CanonicalPartyCentralDrilldownInput = Omit<
  PartyCentralDrilldownInput,
  'game' | 'streamer' | 'language' | 'hourUtc' | 'dayOfWeekUtc' | 'limit'
> & {
  game: string[];
  streamer: string[];
  language: string[];
  hourUtc: number[];
  dayOfWeekUtc: number[];
  limit: number;
};

const canonicalStrings = (values: readonly string[] | undefined): string[] =>
  [...new Set(values ?? [])].sort((left, right) =>
    left < right ? -1 : left > right ? 1 : 0
  );

const canonicalNumbers = (values: readonly number[] | undefined): number[] =>
  [...new Set(values ?? [])].sort((left, right) => left - right);

export function canonicalizePartyCentralQueryInput(
  input: PartyCentralQueryInput
): CanonicalPartyCentralQueryInput {
  const displaysTime = input.x === 'time' || input.y === 'time';
  return {
    measure: input.measure,
    ...(input.x ? { x: input.x } : {}),
    ...(input.y ? { y: input.y } : {}),
    ...(input.grain && displaysTime ? { grain: input.grain } : {}),
    from: new Date(input.from).toISOString(),
    to: new Date(input.to).toISOString(),
    game: canonicalStrings(input.game),
    streamer: canonicalStrings(input.streamer),
    language: canonicalStrings(input.language),
    hourUtc: [],
    dayOfWeekUtc: [],
    ...(input.xMode ? { xMode: input.xMode } : {}),
    ...(input.yMode ? { yMode: input.yMode } : {}),
    xIds: canonicalStrings(input.xIds),
    yIds: canonicalStrings(input.yIds),
    ...(input.pageAxis ? { pageAxis: input.pageAxis } : {}),
    page: input.page ?? 1,
  };
}

export function canonicalizePartyCentralEntitiesInput(
  input: PartyCentralEntitiesInput
): CanonicalPartyCentralEntitiesInput {
  const q = input.q?.trim();
  return {
    type: input.type,
    ...(q ? { q } : {}),
    ids: canonicalStrings(input.ids),
    limit: input.limit ?? 20,
  };
}

export function canonicalizePartyCentralDrilldownInput(
  input: PartyCentralDrilldownInput
): CanonicalPartyCentralDrilldownInput {
  const captureId = input.captureId?.trim();
  return {
    from: new Date(input.from).toISOString(),
    to: new Date(input.to).toISOString(),
    ...(captureId ? { captureId } : {}),
    game: canonicalStrings(input.game),
    streamer: canonicalStrings(input.streamer),
    language: canonicalStrings(input.language),
    hourUtc: canonicalNumbers(input.hourUtc),
    dayOfWeekUtc: canonicalNumbers(input.dayOfWeekUtc),
    limit: input.limit ?? PARTY_CENTRAL_LIMITS.maxDrilldownRows,
  };
}

function queryParams(
  input: CanonicalPartyCentralQueryInput
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {
    measure: input.measure,
    from: input.from,
    to: input.to,
  };
  const singles = {
    x: input.x,
    y: input.y,
    grain: input.grain,
    xMode: input.xMode,
    yMode: input.yMode,
    pageAxis: input.pageAxis,
  };
  Object.entries(singles).forEach(([key, value]) => {
    if (value !== undefined) params[key] = value;
  });
  if (input.page !== 1) params.page = String(input.page);

  const repeated: Record<string, readonly (string | number)[]> = {
    game: input.game,
    streamer: input.streamer,
    language: input.language,
    xIds: input.xIds,
    yIds: input.yIds,
  };
  Object.entries(repeated).forEach(([key, values]) => {
    if (values.length > 0) params[key] = values.map(String);
  });
  return params;
}

function entityParams(
  input: CanonicalPartyCentralEntitiesInput
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {
    type: input.type,
    ids: input.ids,
    ...(input.q ? { q: input.q } : {}),
    limit: String(input.limit),
  };
  if (input.ids.length === 0) delete params.ids;
  return params;
}

function drilldownParams(
  input: CanonicalPartyCentralDrilldownInput,
  cursor?: string
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {
    from: input.from,
    to: input.to,
    limit: String(input.limit),
  };
  if (input.captureId) params.captureId = input.captureId;
  if (cursor) params.cursor = cursor;

  const repeated: Record<string, readonly (string | number)[]> = {
    game: input.game,
    streamer: input.streamer,
    language: input.language,
    hourUtc: input.hourUtc,
    dayOfWeekUtc: input.dayOfWeekUtc,
  };
  Object.entries(repeated).forEach(([key, values]) => {
    if (values.length > 0) params[key] = values.map(String);
  });
  return params;
}

type PartyCentralFetchOptions = Pick<
  BaseFetchOptions<undefined>,
  'params' | 'signal'
>;

/**
 * Party Central is a public, read-only analytics client. In development its
 * requests stay same-origin so Vite can relay only the allowlisted endpoints
 * to production without weakening production CORS.
 */
function partyCentralFetch<TRes>(
  path: `/party-central/${string}`,
  options: PartyCentralFetchOptions = {}
) {
  return baseFetch<undefined, TRes>(path, {
    ...options,
    apiOrigin: import.meta.env.DEV ? window.location.origin : undefined,
    credentials: 'omit',
  });
}

export async function getPartyCentralMeta(
  signal?: AbortSignal
): Promise<PartyCentralMetaResponse> {
  const response = await partyCentralFetch<PartyCentralMetaResponse>(
    '/party-central/meta',
    { signal }
  );
  return response.data;
}

export async function getPartyCentralQuery(
  input: PartyCentralQueryInput,
  options: { signal?: AbortSignal; expectedCaptureId: string }
): Promise<PartyCentralQueryResponse> {
  const canonical = canonicalizePartyCentralQueryInput(input);
  const response = await partyCentralFetch<PartyCentralQueryResponse>(
    '/party-central/query',
    {
      params: queryParams(canonical),
      signal: options.signal,
    }
  );
  if (response.data.meta.asOfCaptureId !== options.expectedCaptureId) {
    throw new PartyCentralCaptureMismatchError(
      options.expectedCaptureId,
      response.data.meta.asOfCaptureId
    );
  }
  return response.data;
}

export async function searchPartyCentralEntities(
  input: PartyCentralEntitiesInput,
  signal?: AbortSignal
): Promise<PartyCentralEntitiesResponse> {
  const canonical = canonicalizePartyCentralEntitiesInput(input);
  const response = await partyCentralFetch<PartyCentralEntitiesResponse>(
    '/party-central/entities',
    {
      params: entityParams(canonical),
      signal,
    }
  );
  return response.data;
}

/**
 * Counts the streams the current scope covers. Sent as a full query request so
 * the server counts exactly what a query would scan, including selected axis
 * members that narrow the facts.
 */
export async function getPartyCentralProbe(
  input: PartyCentralQueryInput,
  signal?: AbortSignal
): Promise<PartyCentralProbeResponse> {
  const canonical = canonicalizePartyCentralQueryInput(input);
  const response = await partyCentralFetch<PartyCentralProbeResponse>(
    '/party-central/probe',
    {
      params: queryParams(canonical),
      signal,
    }
  );
  return response.data;
}

export async function getPartyCentralDrilldown(
  input: PartyCentralDrilldownInput,
  options: { cursor?: string; signal?: AbortSignal } = {}
): Promise<PartyCentralDrilldownResponse> {
  const canonical = canonicalizePartyCentralDrilldownInput(input);
  const response = await partyCentralFetch<PartyCentralDrilldownResponse>(
    '/party-central/drilldown',
    {
      params: drilldownParams(canonical, options.cursor),
      signal: options.signal,
    }
  );
  return response.data;
}
